import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Trophy, Calendar, Search, User, Shield, Sun, Moon, MessageSquare, Users, Building2, Euro, MapPin, BarChart3, Settings, HelpCircle } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { NotificationBell } from '@/components/NotificationBell';
import { useLocation } from 'react-router-dom';

const playerNavItems = [
  { to: '/dashboard', icon: Home, label: 'Home' },
  { to: '/leagues', icon: Trophy, label: 'Leghe' },
  { to: '/matches', icon: Calendar, label: 'Partite' },
  { to: '/facilities', icon: Building2, label: 'Strutture' },
  { to: '/announcements', icon: MessageSquare, label: 'Annunci' },
  { to: '/referees', icon: Users, label: 'Arbitri' },
  { to: '/search', icon: Search, label: 'Cerca' },
  { to: '/profile', icon: User, label: 'Profilo' },
];

const getFacilityNavItems = (facilityId) => [
  { to: `/facilities/${facilityId}/dashboard`, icon: BarChart3, label: 'Dashboard' },
  { to: `/facilities/${facilityId}/dashboard#bookings`, icon: Calendar, label: 'Prenotazioni' },
  { to: `/facilities/${facilityId}/dashboard#payments`, icon: Euro, label: 'Pagamenti' },
  { to: `/facilities/${facilityId}/dashboard#fields`, icon: MapPin, label: 'Campi' },
  { to: `/facilities/${facilityId}`, icon: Building2, label: 'Pagina Pubblica' },
  { to: `/facilities/${facilityId}/edit`, icon: Settings, label: 'Impostazioni' },
  { to: '/profile', icon: User, label: 'Profilo' },
];

export const Sidebar = () => {
  const { user, facilityData } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const isFacilityOwner = user?.account_type === 'facility' && facilityData?.facility_id;
  const navItems = isFacilityOwner
    ? getFacilityNavItems(facilityData.facility_id)
    : playerNavItems;

  // Determina la voce attiva per facility owner
  let activeIndex = -1;
  if (isFacilityOwner) {
    const path = location.pathname;
    const hash = location.hash.replace('#', '');
    const base = `/facilities/${facilityData.facility_id}/dashboard`;
    if (path.startsWith(base)) {
      if (!hash || hash === 'overview') activeIndex = 0;
      else if (hash === 'bookings') activeIndex = 1;
      else if (hash === 'payments') activeIndex = 2;
      else if (hash === 'fields') activeIndex = 3;
    } else {
      // Pagina pubblica
      if (path === `/facilities/${facilityData.facility_id}`) activeIndex = 4;
      // Impostazioni
      else if (path === `/facilities/${facilityData.facility_id}/edit`) activeIndex = 5;
      // Profilo
      else if (path === '/profile') activeIndex = 6;
    }
  }

  return (
    <aside 
      className="safe-sidebar hidden md:flex flex-col w-64 border-r bg-card sticky top-0"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-6 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="LeagueHub" className="h-8 w-auto" />
          <h1 className="text-xl font-bold tracking-tight">
            League<span className="text-primary">Hub</span>
          </h1>
        </div>
        <NotificationBell />
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
        {navItems.map((item, i) => (
          <React.Fragment key={item.to}>
          {!isFacilityOwner && i === 3 && <p className="px-4 pt-5 pb-2 text-xs font-medium text-muted-foreground">Esplora e account</p>}
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                isFacilityOwner
                  ? (i === activeIndex
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground")
                  : (isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground")
              )
            }
            data-testid={`sidebar-${item.label.toLowerCase()}`}
          >
            <item.icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
          </React.Fragment>
        ))}

        {/* Admin link */}
        {user?.role === 'admin' && (
          <NavLink
            to="/admin"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
            data-testid="sidebar-admin"
          >
            <Shield className="h-5 w-5" />
            <span className="font-medium">Admin</span>
          </NavLink>
        )}

        {/* Help link */}
        <NavLink
          to="/help"
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
            isActive
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
          data-testid="sidebar-help"
        >
          <HelpCircle className="h-5 w-5" />
          <span className="font-medium">Aiuto</span>
        </NavLink>
      </nav>

      {/* Theme toggle */}
      <div className="p-4 border-t">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? (
            <>
              <Sun className="h-5 w-5" />
              <span>Tema Chiaro</span>
            </>
          ) : (
            <>
              <Moon className="h-5 w-5" />
              <span>Tema Scuro</span>
            </>
          )}
        </Button>
      </div>

      {/* User info */}
      {user && (
        <div className="p-4 border-t">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email || 'Senza email'}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
